import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Button, 
  Input, 
  Spin, 
  Result, 
  Tag, 
  Descriptions, 
  Space, 
  Typography, 
  Alert,
  Row,
  Col,
  Divider,
  message,
  Modal
} from 'antd';
import { 
  ScanOutlined, 
  ReloadOutlined, 
  InfoCircleOutlined,
  EnvironmentOutlined,
  FireOutlined,
  ThunderboltOutlined,
  WifiOutlined,
  SignalFilled,
  QrcodeOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { SidebarLayout } from '../components/SidebarLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import QrScanner from 'react-qr-scanner';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';

const { Title, Text } = Typography;

export const QRCodeScanner = () => {
  const [loading, setLoading] = useState(false);
  const [deviceData, setDeviceData] = useState(null);
  const [error, setError] = useState(null);
  const [deveui, setDeveui] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  const [showScanner, setShowScanner] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const toggleCamera = () => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };
  const inputRef = useRef(null);
  const { selectedClient } = useAuth();
  const [hasWebcamSupport, setHasWebcamSupport] = useState(true);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [lastScanTimestamp, setLastScanTimestamp] = useState(0);
  const scanLock = useRef(false);
  const [displayStatuses, setDisplayStatuses] = useState([]);

  const cleanDevEUI = (input) => {
    if (!input) return '';
    const cleanedInput = input.trim().toUpperCase();
    return cleanedInput.replace(/[^A-Z0-9]/g, '');
  };
  
  const isValidDevEUI = (deveui) => {
    if (!deveui || !/^[A-Z0-9]+$/.test(deveui)) return false;
  
    return deveui.length >= 8 && deveui.length <= 16;
  };
    useEffect(() => {
    const checkCameraSupport = () => {
      // Verifica apenas se a API está disponível, sem ativar a câmera
      if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setHasWebcamSupport(false);
        return;
      }
      
      setHasWebcamSupport(true);
    };
    
    checkCameraSupport();
    
    axios.get(`${process.env.REACT_APP_API_URL}/display_status`)
      .then(res => setDisplayStatuses(res.data))
      .catch(() => setDisplayStatuses([]));
  }, []);
  
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  const handleQRScan = (result) => {
    if (scanLock.current || loading) return;
    console.log('Resultado do scan:', result);
    
    let scannedData = null;
    if (result && result.text) {
      scannedData = result.text.trim();
    } else if (result && typeof result === 'string') {
      scannedData = result.trim();
    }
    
    if (scannedData) {
      const cleanedData = cleanDevEUI(scannedData);
      
      if (cleanedData !== scannedData) {
        console.log('QR Code continha caracteres não alfanuméricos. Original:', scannedData, 'Limpo:', cleanedData);
      }
      
      if (!isValidDevEUI(cleanedData)) {
        console.warn('QR Code não contém um DevEUI válido');
        messageApi.error('Código QR inválido. O DevEUI deve conter entre 8 e 16 caracteres alfanuméricos.');
        return;
      }
      
      scannedData = cleanedData;
      const now = Date.now();
      if (scannedData === lastScannedCode && (now - lastScanTimestamp) < 3000) {
        console.log('Ignorando leitura duplicada do QR Code');
        return;
      }
      scanLock.current = true;
      console.log('QR Code detectado (após limpeza):', scannedData);
      
      setDeveui(scannedData);
      setLastScannedCode(scannedData);
      setLastScanTimestamp(now);
      setShowScanner(false);
      setCameraError(null);
      messageApi.success('QR Code lido com sucesso!');
      
      setTimeout(() => {
        handleScan(scannedData).finally(() => {
          scanLock.current = false;
        });
      }, 500);
    }
  };
  const handleQRError = (err) => {
    console.warn('Erro no scanner QR:', err);
    if (err?.name === 'NotAllowedError') {
      setCameraError('Permissão de câmera negada. Por favor, permita o acesso à câmera.');
    } else if (err?.name === 'NotFoundError') {
      setCameraError('Câmera não encontrada. Verifique se há uma câmera conectada.');
    } else if (err?.message?.includes('getUserMedia') || err?.toString().includes('getUserMedia')) {
      setCameraError('Seu navegador não suporta acesso à câmera ou a API está indisponível.');
    } else {
      setCameraError('Erro ao acessar a câmera. Verifique as permissões e tente novamente.');
    }
  };  const startScanner = async () => {
    if (!hasWebcamSupport) {
      setCameraError('Seu navegador não suporta acesso à câmera. Tente em outro navegador ou dispositivo.');
      setShowScanner(true);
      return;
    }
    
    // Verificação mais específica apenas quando o usuário solicita o scanner
    try {
      await navigator.mediaDevices.getUserMedia({ video: true }).then(stream => {
        // Para a câmera imediatamente após verificar que funciona
        stream.getTracks().forEach(track => track.stop());
      });
      setShowScanner(true);
      setCameraError(null);
    } catch (err) {
      console.warn('Erro ao verificar câmera:', err);
      let errorMessage = 'Erro ao acessar a câmera. Verifique as permissões e tente novamente.';
      
      if (err?.name === 'NotAllowedError') {
        errorMessage = 'Permissão de câmera negada. Por favor, permita o acesso à câmera.';
      } else if (err?.name === 'NotFoundError') {
        errorMessage = 'Câmera não encontrada. Verifique se há uma câmera conectada.';
      }
      
      setCameraError(errorMessage);
      setShowScanner(true);
    }
  };

  const stopScanner = () => {
    setShowScanner(false);
    setCameraError(null);
  };  const handleScan = async (scannedDeveui = null) => {
    let deviceId = scannedDeveui || deveui.trim();
    
    if (!deviceId) {
      messageApi.error('Por favor, insira um DevEUI válido');
      return;
    }
    
    deviceId = cleanDevEUI(deviceId);
    
    if (!isValidDevEUI(deviceId)) {
      messageApi.error('DevEUI inválido. Deve conter entre 8 e 16 caracteres alfanuméricos (A-Z, 0-9)');
      return;
    }

    const currentTime = Date.now();
    if (lastScannedCode === deviceId && currentTime - lastScanTimestamp < 3000) {
      console.log('Ignorando busca duplicada do mesmo QR Code');
      return;
    }

    if (loading) {
      console.log('Já existe uma busca em andamento, ignorando nova solicitação');
      return;
    }

    setLoading(true);
    setError(null);
    setDeviceData(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/devices/scan/${deviceId}`
      );
      setDeviceData(response.data);
      messageApi.success('Dispositivo encontrado!');
      
      setLastScannedCode(deviceId);
      setLastScanTimestamp(currentTime);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Erro ao buscar dispositivo';
      setError(errorMsg);
      messageApi.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };const handleInputChange = (e) => {
    const inputValue = e.target.value;
    const alphanumericOnly = cleanDevEUI(inputValue);
    
    if (alphanumericOnly !== inputValue && inputValue.length > alphanumericOnly.length) {
      messageApi.warning('DevEUI deve conter apenas caracteres alfanuméricos (A-Z, 0-9)');
    }
    
    setDeveui(alphanumericOnly);
  };
  const handleKeyPress = (e) => {
    if (!/[a-zA-Z0-9]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace') {
      e.preventDefault();
      return;
    }
    
    if (e.key === 'Enter') {
      handleScan();
    }
  };const clearResults = () => {
    setDeviceData(null);
    setError(null);
    setDeveui('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const formatTemperature = (temp) => {
    if (temp === null || temp === undefined) return 'N/A';
    return `${temp}°C`;
  };

  const formatBattery = (battery) => {
    if (battery === null || battery === undefined) return 'N/A';
    return `${battery}mV`;
  };
  const formatRSSI = (rssi) => {
    if (rssi === null || rssi === undefined) return 'N/A';
    return `${rssi} dBm`;
  };

  const formatSNR = (snr) => {
    if (snr === null || snr === undefined) return 'N/A';
    return `${snr} dB`;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'Available': 'green',
      'In Test': 'orange',
      'Produced': 'blue',
      'Delivered': 'cyan',
      'Installed': 'purple',
      'Linked': 'success',
      'Removed': 'red'
    };
    return statusColors[status] || 'default';
  };

  const getTypeColor = (type) => {
    const typeColors = {
      'Control Panel': 'blue',
      'Wheel Hub Sensor': 'green'
    };
    return typeColors[type] || 'default';
  };

  const getDisplayStatusColor = (id) => {
    const statusColors = {
      'Vago': 'default',
      'Instalado': 'green',
    };
    return statusColors[id] || 'default';
  };
  return (
    <SidebarLayout>
      {contextHolder}
      <div style={{ 
        padding: window.innerWidth < 800 ? 0 : 24, 
        maxWidth: 1200, 
        margin: '0 auto' 
      }}>        <Title level={2} style={{ 
          textAlign: 'center', 
          marginBottom: window.innerWidth < 768 ? 24 : 32,
          fontSize: window.innerWidth < 768 ? 20 : 32
        }}>
          <h1 style={{fontSize: 30}}>Consultar Dispositivo</h1>
        </Title>
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={24} md={16} lg={16}>              <Input
                ref={inputRef}
                size="large"
                placeholder="Escaneie QR Code ou digite o DevEUI..."
                value={deveui}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                prefix={<ScanOutlined />}
                maxLength={16}
                style={{ 
                  fontSize: 16,
                  borderRadius: 8
                }}
                disabled={loading}
                // Garantir que apenas caracteres alfanuméricos sejam aceitos
                onPaste={(e) => {
                  const pasteData = e.clipboardData.getData('text');
                  const alphanumericOnly = cleanDevEUI(pasteData);
                  if (alphanumericOnly !== pasteData) {
                    e.preventDefault();
                    setDeveui(alphanumericOnly);
                  }
                }}
              />
            </Col>
            
            <Col xs={12} sm={12} md={4} lg={4}>
              <Button
                type="primary"
                size="large"
                onClick={() => handleScan()}
                loading={loading}
                block
                style={{ borderRadius: 8 }}
              >
                Buscar
              </Button>
            </Col>
            <Col xs={12} sm={12} md={4} lg={4}>
              <Button
                size="large"
                onClick={clearResults}
                icon={<ReloadOutlined />}
                block
                style={{ borderRadius: 8 }}
              >
                Limpar
              </Button>
            </Col>            <Col xs={24} sm={24} md={24} lg={24}>
              <Button
                size="large"
                onClick={startScanner}
                block
                style={{ 
                  borderRadius: 8, 
                  height: '120px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                disabled={loading}
              >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <QrcodeOutlined style={{ fontSize: '48px', marginBottom: '8px' }} />
                  <span style={{ fontSize: '16px', fontWeight: '500' }}>Scanner de QR Code</span>
                </div>
              </Button>
            </Col></Row>
          
        </Card>        {/* QR Code Scanner Modal */}
        <Modal
          title={
            <Space>
              <QrcodeOutlined />
              Scanner de QR Code
            </Space>
          }
          open={showScanner}
          onCancel={stopScanner}
          width={700}
          centered
          destroyOnClose
          maskClosable={false}
          footer={[
            <Button key="toggle" onClick={toggleCamera}>
              Alternar Câmera
            </Button>,
            <Button key="close" onClick={stopScanner} icon={<CloseOutlined />}>
              Fechar Scanner
            </Button>
          ]}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            {cameraError ? (
              <>                <Alert
                  message="Erro na Câmera"
                  description={cameraError}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                  action={
                    <Button size="small" onClick={startScanner}>
                      Tentar Novamente
                    </Button>
                  }
                />
                <div style={{ marginTop: 16 }}>
                  <Text>Você também pode inserir o código manualmente:</Text>
                  <Input.Search
                    placeholder="Digite o DevEUI manualmente..."
                    enterButton="Buscar"
                    size="large"
                    style={{ marginTop: 8 }}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      const alphanumericOnly = cleanDevEUI(inputValue);
                      if (alphanumericOnly !== inputValue && inputValue.length > alphanumericOnly.length) {
                        messageApi.warning('DevEUI deve conter apenas caracteres alfanuméricos (A-Z, 0-9)');
                      }
                      setDeveui(alphanumericOnly);
                    }}
                    onSearch={(value) => {
                      const cleanValue = cleanDevEUI(value);
                      if (isValidDevEUI(cleanValue)) {
                        setShowScanner(false);
                        setTimeout(() => handleScan(cleanValue), 500);
                      } else {
                        messageApi.error('Por favor, insira um DevEUI válido (8-16 caracteres alfanuméricos)');
                      }
                    }}
                    maxLength={16}
                  />
                </div>
              </>
            ) : (<>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px',
                  border: '2px dashed #1890ff',
                  borderRadius: '8px',
                  backgroundColor: '#fafafa',
                  position: 'relative',
                  overflow: 'hidden'
                }}>                  {hasWebcamSupport ? (
                    <QrScanner
                      delay={1000}
                      style={{
                        height: 400,
                        width: '100%'
                      }}
                      onError={handleQRError}
                      onScan={handleQRScan}
                      facingMode={facingMode}
                      legacyMode={false}
                      constraints={{
                        video: {
                          facingMode: facingMode,
                          width: { ideal: 1280 },
                          height: { ideal: 720 }
                        }
                      }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: 20 }}>                      <Alert
                        message="Câmera não disponível"
                        description="Seu navegador não suporta acesso à câmera ou está bloqueado. Tente em outro dispositivo ou navegador."
                        type="error"
                        showIcon
                      />
                      <div style={{ marginTop: 16 }}>
                        <Text>Você também pode inserir o código manualmente:</Text>
                        <Input.Search
                          placeholder="Digite o DevEUI manualmente..."
                          enterButton="Buscar"
                          size="large"
                          style={{ marginTop: 8 }}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            const alphanumericOnly = cleanDevEUI(inputValue);
                            if (alphanumericOnly !== inputValue && inputValue.length > alphanumericOnly.length) {
                              messageApi.warning('DevEUI deve conter apenas caracteres alfanuméricos (A-Z, 0-9)');
                            }
                            setDeveui(alphanumericOnly);
                          }}
                          onSearch={(value) => {
                            const cleanValue = cleanDevEUI(value);
                            if (isValidDevEUI(cleanValue)) {
                              setShowScanner(false);
                              setTimeout(() => handleScan(cleanValue), 500);
                            } else {
                              messageApi.error('Por favor, insira um DevEUI válido (8-16 caracteres alfanuméricos)');
                            }
                          }}
                          maxLength={16}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Overlay para ajudar no posicionamento */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '250px',
                    height: '250px',
                    border: '3px solid #1890ff',
                    borderRadius: '12px',
                    pointerEvents: 'none',
                    background: 'transparent',
                    zIndex: 10
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '-10px',
                      width: '30px',
                      height: '30px',
                      border: '4px solid #1890ff',
                      borderRight: 'transparent',
                      borderBottom: 'transparent',
                      borderRadius: '4px 0 0 0'
                    }} />
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '-10px',
                      width: '30px',
                      height: '30px',
                      border: '4px solid #1890ff',
                      borderLeft: 'transparent',
                      borderBottom: 'transparent',
                      borderRadius: '0 4px 0 0'
                    }} />
                    <div style={{
                      position: 'absolute',
                      bottom: '-10px',
                      left: '-10px',
                      width: '30px',
                      height: '30px',
                      border: '4px solid #1890ff',
                      borderRight: 'transparent',
                      borderTop: 'transparent',
                      borderRadius: '0 0 0 4px'
                    }} />
                    <div style={{
                      position: 'absolute',
                      bottom: '-10px',
                      right: '-10px',
                      width: '30px',
                      height: '30px',
                      border: '4px solid #1890ff',
                      borderLeft: 'transparent',
                      borderTop: 'transparent',
                      borderRadius: '0 0 4px 0'
                    }} />
                  </div>
                </div>
                
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    📱 Posicione o QR Code dentro da área destacada
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Mantenha o código estável por alguns segundos
                  </Text>
                </div>
              </>
            )}
          </div>
        </Modal>

        {/* Loading State */}
        {loading && (
          <Card style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Buscando informações do dispositivo...</Text>
            </div>
          </Card>
        )}

        {/* Error State */}
        {error && !loading && (
          <Result
            status="error"
            title="Dispositivo não encontrado"
            subTitle={error}
            extra={[
              <Button type="primary" key="retry" onClick={() => handleScan()}>
                Tentar Novamente
              </Button>,
              <Button key="clear" onClick={clearResults}>
                Nova Busca
              </Button>
            ]}
          />
        )}

        {/* Success State - Device Information */}
        {deviceData && !loading && !error && (
          <div>
            {/* Device Basic Info */}
            <Card 
              title={
                <Space>
                  <InfoCircleOutlined />
                  Informações do Dispositivo
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="DevEUI">
                      <Text strong style={{ fontSize: 16 }}>
                        {deviceData.device.dsdeveui}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Tag">
                      {deviceData.device.dstag || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Tipo">
                      <Tag color={getTypeColor(deviceData.device.type)}>
                        {deviceData.device.type}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={getDisplayStatusColor(deviceData.device.status)}>
                        {deviceData.device.status}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Cliente">
                      <Text>{deviceData.device.customer}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>

            {/* Position Information */}
            {deviceData.current_position && (
              <Card 
                title={
                  <Space>
                    <EnvironmentOutlined />
                    Posição Atual
                  </Space>
                }
                style={{ marginBottom: 24 }}
              >
                <Alert
                  message={deviceData.current_position.position_description}
                  description={
                    <div>
                      <Text strong>Placa: </Text>
                      <Text>{deviceData.current_position.dsplate}</Text>
                      {deviceData.current_position.dsdrivername && (
                        <>
                          <br />
                          <Text strong>Motorista: </Text>
                          <Text>{deviceData.current_position.dsdrivername}</Text>
                        </>
                      )}
                    </div>
                  }
                  type="info"
                  showIcon
                />
              </Card>
            )}

            {/* Sensor Data */}
            {deviceData.sensor_data && (
              <Card                title={
                  <Space>
                    <FireOutlined />
                    Dados do Sensor
                  </Space>
                }
                style={{ marginBottom: 24 }}
              >                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={12} md={6}>
                    <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f6f8ff' }}>
                      <FireOutlined style={{ fontSize: 20, color: '#1890ff', marginBottom: 8 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>Temperatura</Text>
                        <br />
                        <Text strong style={{ fontSize: 16 }}>
                          {formatTemperature(deviceData.sensor_data.temperature)}
                        </Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={12} md={6}>
                    <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f6ffed' }}>
                      <ThunderboltOutlined style={{ fontSize: 20, color: '#52c41a', marginBottom: 8 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>Bateria</Text>
                        <br />
                        <Text strong style={{ fontSize: 16 }}>
                          {formatBattery(deviceData.sensor_data.battery)}
                        </Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={12} md={6}>
                    <Card size="small" style={{ textAlign: 'center', backgroundColor: '#fff7e6' }}>
                      <ScanOutlined style={{ fontSize: 20, color: '#fa8c16', marginBottom: 8 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>RSSI</Text>
                        <br />
                        <Text strong style={{ fontSize: 16 }}>
                          {formatRSSI(deviceData.sensor_data.rssi)}
                        </Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={12} md={6}>
                    <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f0f9ff' }}>
                      <SignalFilled style={{ fontSize: 20, color: '#1677ff', marginBottom: 8 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>SNR</Text>
                        <br />
                        <Text strong style={{ fontSize: 16 }}>
                          {formatSNR(deviceData.sensor_data.snr)}
                        </Text>
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={24} md={24}>
                    <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f9f0ff' }}>
                      <InfoCircleOutlined style={{ fontSize: 20, color: '#722ed1', marginBottom: 8 }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>Última Atualização</Text>
                        <br />
                        <Text strong style={{ fontSize: 14 }}>
                          {deviceData.sensor_data.last_update 
                            ? new Date(deviceData.sensor_data.last_update).toLocaleString('pt-BR')
                            : 'N/A'
                          }
                        </Text>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Card>            )}

            {/* Historical Data Chart */}
            {deviceData.historical_data && deviceData.historical_data.length > 0 && (
              <Card 
                title={
                  <Space>
                    <InfoCircleOutlined />
                    Dados dos Últimos 30 Dias
                  </Space>
                }
                style={{ marginBottom: 24 }}
              >                <div style={{ height: window.innerWidth < 768 ? 300 : 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={deviceData.historical_data}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 20,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return window.innerWidth < 768 ? 
                            `${date.getDate()}/${date.getMonth() + 1}` :
                            `${date.getDate()}/${date.getMonth() + 1}`;
                        }}
                        interval={window.innerWidth < 768 ? 'preserveStartEnd' : 0}
                      />
                      <YAxis 
                        yAxisId="temp"
                        tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                        formatter={(value, name) => [`${value} ${name === 'temperature' ? '°C' : ''}`, name]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="temperature" 
                        stroke="#1890ff" 
                        strokeWidth={2}
                        dot={false}
                        yAxisId="temp"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

// Exportar também com o nome antigo para manter compatibilidade
export { QRCodeScanner as QRNFCScanner };
